<template>
  <!-- ========================= -->
  <!-- TAB 1: BASIC              -->
  <!-- ========================= -->
  <div>
    <div class="row mt-2"></div>
    <p class="bb-hdg mt-2 mb-3">Basic Details</p>
    <!-- -------------- -->
    <!--      ROW 1     -->
    <!-- -------------- -->
    <div class="row mt-2 mb-2">
      <!-- --------------------- -->
      <!--   row number (r/o)    -->
      <!-- --------------------- -->
      <div
        v-if="activeItem.id !== 0"
        class="col-3"
      >
        <div class="form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="rowNumber"
              id="row"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="row"
              >Row</label
            >
          </div>
        </div>
      </div>
      <!-- ------------------- -->
      <!--   item name (r/o)   -->
      <!-- ------------------- -->
      <div
        v-if="activeItem.id !== 0"
        class="col"
      >
        <div class="form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control"
              :value="itemName"
              id="item_name"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="item_name"
              >Item Name</label
            >
          </div>
        </div>
      </div>
      <!-- --------------------- -->
      <!--   item name (input)   -->
      <!-- --------------------- -->
      <div
        v-if="activeItem.id === 0"
        class="col"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :modelValue="localItem.name"
              :options="itemNames"
              label="label"
              :reduce="(option) => option.value"
              class="form-control"
              :class="{ 'is-invalid': errorFields.includes('name') }"
              @keydown="onKeyDown($event)"
              @update:modelValue="onItemSelect"
              id="name"
              @click="clearInputError('name')"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  :required="!localItem.name"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label"
              for="name"
              >Item Name</label
            >
          </div>
        </div>
      </div>
    </div>
    <!-- -------------- -->
    <!--      ROW 2     -->
    <!-- -------------- -->
    <div
      v-if="hasItemName"
      class="row mt-2 mb-2"
    >
      <!-- ------------------------------- -->
      <!--   type / equipment type (r/o)   -->
      <!-- ------------------------------- -->
      <div class="col-5 form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="displayItemType"
            id="type"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="type"
            >Item Type</label
          >
        </div>
      </div>
      <!-- -------------------------------- -->
      <!--   currency cost per item (r/o)   -->
      <!-- -------------------------------- -->
      <div
        v-if="this.resolvedItemType !== 'Money' && !isCountableItem"
        class="col-3 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="displayCurrencyCost"
            id="currency_cost"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="currency_cost"
            >Cost per Item</label
          >
        </div>
      </div>
      <!-- ------------------- -->
      <!--   item enc (r/o)    -->
      <!-- ------------------- -->
      <div
        v-if="!isCountableItem && !isTallyableItem"
        class="col-4 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="formatEnc(activeItem.enc)"
            id="enc"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="enc"
            >Enc per Item</label
          >
        </div>
      </div>
    </div>
    <!-- ---------------------------- -->
    <!--   WHERE ITEM NAME PRESENT   -->
    <!-- ---------------------------- -->
    <div v-if="hasItemName">
      <!-- -------------- -->
      <!--      ROW 3     -->
      <!-- -------------- -->
      <div class="row mt-2 mb-2">
        <!-- ---------------------- -->
        <!--   campaign ref (r/o)   -->
        <!-- ---------------------- -->
        <div
          v-if="showCampaignRef"
          class="col-3 form-group"
        >
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="campaignRefValue"
              id="campaign_ref"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="campaign_ref"
              >Campaign Ref</label
            >
          </div>
        </div>
        <!-- --------------------- -->
        <!--   armour state (r/o)  -->
        <!-- --------------------- -->
        <div
          v-if="isWeaponOrArmour"
          class="col-5 form-group"
        >
          <div class="form-floating">
            <input
              readonly
              :class="[
                'form-control',
                'text-center',
                activeItem.broken ? 'bb-item-warning' : '',
              ]"
              :value="computeItemState"
              id="state"
              disabled
            />
            <label
              :class="[
                'bb-float-label',
                'optional',
                activeItem.broken ? 'bb-item-warning-label' : '',
              ]"
              for="state"
            >
              State
            </label>
          </div>
        </div>
      </div>
      <!-- ----------------- -->
      <!--   DESCRIPTIONS    -->
      <!-- ----------------- -->
      <p class="bb-sub-hdg mb-2 mt-2">Descriptions</p>
      <!-- -------------- -->
      <!--     ROW 4      -->
      <!-- -------------- -->
      <div class="row mt-2 mb-2">
        <!-- -------------------------- -->
        <!--  short description (r/o)   -->
        <!-- -------------------------- -->
        <div class="col form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-left"
              :value="activeItem.short_description"
              id="short_description"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="short_description"
              >Short Description</label
            >
          </div>
        </div>
      </div>
      <div
        v-if="documentUrl && isOfficeViewerDocument"
        class="row mt-2 mb-2"
      >
        <div class="col-12">
          <small class="text-muted d-block">
            {{ documentViewerMessage }}
          </small>
        </div>
      </div>
      <!-- -------------- -->
      <!--     ROW 5      -->
      <!-- -------------- -->
      <div class="row mt-2 mb-2">
        <!-- -------------------- -->
        <!--  description (r/o)   -->
        <!-- -------------------- -->
        <div class="form-group">
          <div class="form-floating">
            <textarea
              id="description"
              name="description"
              type="text"
              rows="4"
              class="form-control bb-textarea-with-label bb-card-description-control"
              :value="activeItem.description"
              disabled
            ></textarea>
            <label
              class="bb-float-label optional"
              for="description"
              >Description</label
            >
          </div>
        </div>
      </div>
      <!-- -------------- -->
      <!--     ROW 6      -->
      <!-- -------------- -->
      <div class="row mt-2 mb-2">
        <!-- ---------------- -->
        <!--  notes (input)   -->
        <!-- ---------------- -->
        <div class="form-group">
          <div class="form-floating">
            <textarea
              id="notes"
              name="notes"
              type="text"
              rows="2"
              class="form-control bb-textarea-with-label bb-card-description-control"
              :value="activeItem.player_notes"
              placeholder="..."
              autocomplete="off"
              @input="onCapitaliseFirstLetter('player_notes', $event)"
            ></textarea>
            <label
              class="bb-float-label optional"
              for="notes"
              >Player Notes</label
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ItemBasicTab',
  props: {
    resolvedItemType: {
      type: String,
      required: true,
    },
    activeItem: {
      type: Object,
      required: true,
    },
    staticItem: {
      type: Object,
      default: null,
    },
    localItem: {
      type: Object,
      required: true,
    },
    hasItemName: {
      type: Boolean,
      required: true,
    },
    rowNumber: {
      type: [Number, String],
      required: true,
    },
    itemName: {
      type: String,
      required: true,
    },
    itemNames: {
      type: Array,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    formatEnc: {
      type: Function,
      required: true,
    },
    showCampaignRef: {
      type: Boolean,
      required: true,
    },
    campaignRefValue: {
      type: [String, Number],
      required: true,
    },
    calcQtyTotEnc: {
      type: Number,
      required: true,
    },
    isWeaponOrArmour: {
      type: Boolean,
      required: true,
    },
    computeItemState: {
      type: String,
      required: true,
    },
    onKeyDown: {
      type: Function,
      required: true,
    },
    onItemSelect: {
      type: Function,
      required: true,
    },
    clearInputError: {
      type: Function,
      required: true,
    },
    onCapitaliseFirstLetter: {
      type: Function,
      required: true,
    },
    delayedEmitUpdate: {
      type: Function,
      required: true,
    },
    onCounterChanged: {
      type: Function,
      required: true,
    },
  },
  computed: {
    displayItemType() {
      const equipmentType =
        this.localItem?.equipment_type ??
        this.activeItem?.equipment_type ??
        'n/a'
      const itemType = this.localItem?.type ?? this.activeItem?.type ?? ''

      return equipmentType !== 'n/a' ? equipmentType : itemType
    },
    displayCurrencyCost() {
      return (
        this.localItem?.currency_cost ?? this.activeItem?.currency_cost ?? ''
      )
    },
    isCountableItem() {
      const value = this.localItem?.countable ?? this.activeItem?.countable

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === 'true' || normalized === '1'
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
    isTallyableItem() {
      const value = this.localItem?.tallyable ?? this.activeItem?.tallyable

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === 'true' || normalized === '1'
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
    documentUrl() {
      return (
        this.staticItem?.reference_document_url ??
        this.buildAbsoluteUrl(this.staticItem?.reference_document ?? '')
      )
    },
    documentName() {
      const serializerName = this.staticItem?.reference_document_name
      if (serializerName) {
        return serializerName
      }

      if (!this.documentUrl) {
        return ''
      }

      const cleanPath = this.documentUrl.split('?')[0]
      const fileName = cleanPath.split('/').pop() || cleanPath

      try {
        return decodeURIComponent(fileName)
      } catch {
        return fileName
      }
    },
    documentExtension() {
      const parts = this.documentName.split('.')
      return (parts.length > 1 ? parts.pop() : '').toLowerCase()
    },
    isOfficeViewerDocument() {
      return ['doc', 'docx'].includes(this.documentExtension)
    },
    documentViewerUrl() {
      return this.staticItem?.reference_document_viewer_url ?? this.documentUrl
    },
    usesExternalViewer() {
      return (
        this.isOfficeViewerDocument &&
        this.documentViewerUrl !== this.documentUrl
      )
    },
    documentViewerMessage() {
      if (this.usesExternalViewer) {
        return 'The document will open in the external Office web viewer.'
      }

      return 'This document will open from the BattleBoard media URL because the external Office viewer cannot reach the current document URL.'
    },
  },
  methods: {
    buildAbsoluteUrl(path) {
      if (!path) {
        return ''
      }

      if (/^https?:\/\//i.test(path)) {
        return path
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const baseUrl = import.meta.env.VITE_API_URL || window.location.origin

      try {
        return new URL(normalizedPath, baseUrl).toString()
      } catch {
        return normalizedPath
      }
    },
  },
}
</script>
